import { useState, useMemo, useEffect } from 'react';

import { Truck, MapPin, Building2, Store as StoreIcon, Plus, Search, Edit2, Trash2, CheckCircle2, XCircle, X, Ban, Route, DollarSign, Users, Wallet, RefreshCw } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@dukene/shared';
import { County, Town, Market, PickupDropoffLocation } from '@dukene/shared';

export default function Shipping() {
    const [activeTab, setActiveTab] = useState<'counties' | 'towns' | 'markets' | 'locations' | 'routes' | 'pricing' | 'appusers'>('counties');
    const [search, setSearch] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<any>(null);
    const queryClient = useQueryClient();

    // Fetch Counties
    const { data: counties = [], isLoading: isLoadingCounties } = useQuery<County[]>({
        queryKey: ['counties'],
        queryFn: () => apiClient.get<County[]>('/superadmin/locations/counties'),
    });

    // Fetch Towns
    const { data: towns = [], isLoading: isLoadingTowns } = useQuery<Town[]>({
        queryKey: ['towns'],
        queryFn: () => apiClient.get<Town[]>('/superadmin/locations/towns'),
    });

    // Fetch Markets
    const { data: markets = [], isLoading: isLoadingMarkets } = useQuery<Market[]>({
        queryKey: ['markets'],
        queryFn: () => apiClient.get<Market[]>('/superadmin/locations/markets'),
    });

    // Fetch Locations
    const { data: locations = [], isLoading: isLoadingLocations } = useQuery<PickupDropoffLocation[]>({
        queryKey: ['locations'],
        queryFn: () => apiClient.get<PickupDropoffLocation[]>('/superadmin/locations/pickup-dropoff'),
    });

    // Mutations
    const createCountyMutation = useMutation({
        mutationFn: (data: Partial<County>) => apiClient.post('/superadmin/locations/counties', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['counties'] });
            handleCloseModal();
        },
    });

    const createTownMutation = useMutation({
        mutationFn: (data: Partial<Town>) => apiClient.post('/superadmin/locations/towns', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['towns'] });
            handleCloseModal();
        },
    });

    const createMarketMutation = useMutation({
        mutationFn: (data: Partial<Market>) => apiClient.post('/superadmin/locations/markets', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['markets'] });
            handleCloseModal();
        },
    });

    const createLocationMutation = useMutation({
        mutationFn: (data: Partial<PickupDropoffLocation>) => apiClient.post('/superadmin/locations/pickup-dropoff', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['locations'] });
            handleCloseModal();
        },
    });

    // Update Mutations
    const updateCountyMutation = useMutation({
        mutationFn: ({ id, data }: { id: string, data: Partial<County> }) => apiClient.put(`/superadmin/locations/counties/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['counties'] });
            handleCloseModal();
        },
    });

    const updateTownMutation = useMutation({
        mutationFn: ({ id, data }: { id: string, data: Partial<Town> }) => apiClient.put(`/superadmin/locations/towns/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['towns'] });
            handleCloseModal();
        },
    });

    const updateMarketMutation = useMutation({
        mutationFn: ({ id, data }: { id: string, data: Partial<Market> }) => apiClient.put(`/superadmin/locations/markets/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['markets'] });
            handleCloseModal();
        },
    });

    const updateLocationMutation = useMutation({
        mutationFn: ({ id, data }: { id: string, data: Partial<PickupDropoffLocation> }) => apiClient.put(`/superadmin/locations/pickup-dropoff/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['locations'] });
            handleCloseModal();
        },
    });

    // Delete Mutations
    const deleteCountyMutation = useMutation({
        mutationFn: (id: string) => apiClient.delete(`/superadmin/locations/counties/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['counties'] }),
    });

    const deleteTownMutation = useMutation({
        mutationFn: (id: string) => apiClient.delete(`/superadmin/locations/towns/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['towns'] }),
    });

    const deleteMarketMutation = useMutation({
        mutationFn: (id: string) => apiClient.delete(`/superadmin/locations/markets/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['markets'] }),
    });

    const deleteLocationMutation = useMutation({
        mutationFn: (id: string) => apiClient.delete(`/superadmin/locations/pickup-dropoff/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['locations'] }),
    });

    const handleCloseModal = () => {
        setIsAddModalOpen(false);
        setEditingItem(null);
    };

    const handleEdit = (item: any) => {
        setEditingItem(item);
        setIsAddModalOpen(true);
    };

    const handleDelete = async (type: string, id: string, name: string) => {
        if (window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
            switch (type) {
                case 'county': await deleteCountyMutation.mutateAsync(id); break;
                case 'town': await deleteTownMutation.mutateAsync(id); break;
                case 'market': await deleteMarketMutation.mutateAsync(id); break;
                case 'location': await deleteLocationMutation.mutateAsync(id); break;
            }
        }
    };

    const handleToggleStatus = async (type: string, item: any) => {
        const newData = { isActive: !item.isActive };
        switch (type) {
            case 'county': await updateCountyMutation.mutateAsync({ id: item.id, data: newData }); break;
            case 'town': await updateTownMutation.mutateAsync({ id: item.id, data: newData }); break;
            case 'market': await updateMarketMutation.mutateAsync({ id: item.id, data: newData }); break;
            case 'location': await updateLocationMutation.mutateAsync({ id: item.id, data: newData }); break;
        }
    };

    // Engine data
    const { data: routes = [], isLoading: isLoadingRoutes } = useQuery<any[]>({
        queryKey: ['shippingRoutes'],
        queryFn: () => apiClient.get('/shipping/routes'),
    });
    const { data: pricingConfig } = useQuery<any>({
        queryKey: ['pricingConfig'],
        queryFn: () => apiClient.get('/shipping/pricing-config'),
    });
    const { data: appUsers = [], isLoading: isLoadingAppUsers } = useQuery<any[]>({
        queryKey: ['appUsers'],
        queryFn: () => apiClient.get('/shipping/app/users'),
    });

    const createRouteMutation = useMutation({
        mutationFn: (data: any) => apiClient.post('/shipping/routes', data),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['shippingRoutes'] }); handleCloseModal(); },
    });
    const updateRouteMutation = useMutation({
        mutationFn: ({ id, data }: any) => apiClient.put(`/shipping/routes/${id}`, data),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['shippingRoutes'] }); handleCloseModal(); },
    });
    const deleteRouteMutation = useMutation({
        mutationFn: (id: string) => apiClient.delete(`/shipping/routes/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shippingRoutes'] }),
    });
    const updatePricingMutation = useMutation({
        mutationFn: (data: any) => apiClient.put('/shipping/pricing-config', data),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['pricingConfig'] }); handleCloseModal(); },
    });
    const createAppUserMutation = useMutation({
        mutationFn: (data: any) => apiClient.post('/shipping/app/users', data),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['appUsers'] }); handleCloseModal(); },
    });
    const updateAppUserMutation = useMutation({
        mutationFn: ({ id, data }: any) => apiClient.put(`/shipping/app/users/${id}`, data),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['appUsers'] }); handleCloseModal(); },
    });
    const deleteAppUserMutation = useMutation({
        mutationFn: (id: string) => apiClient.delete(`/shipping/app/users/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['appUsers'] }),
    });
    const resetPinMutation = useMutation({
        mutationFn: ({ id, newPin }: { id: string; newPin: string }) => apiClient.post(`/shipping/app/users/${id}/reset-pin`, { newPin }),
        onSuccess: () => alert('PIN reset successfully'),
    });
    const runPayoutMutation = useMutation({
        mutationFn: () => apiClient.post('/shipping/payouts/run', {}),
        onSuccess: (data: any) => alert(`Payout batch created — ${data.totalPayouts} payouts queued`),
    });

    const tabs = [
        { id: 'counties' as const, label: 'Counties', icon: MapPin, count: counties.length },
        { id: 'towns' as const, label: 'Towns', icon: Building2, count: towns.length },
        { id: 'markets' as const, label: 'Markets', icon: StoreIcon, count: markets.length },
        { id: 'locations' as const, label: 'Pickup/Dropoff', icon: Truck, count: locations.length },
        { id: 'routes' as const, label: 'Routes', icon: Route, count: routes.length },
        { id: 'pricing' as const, label: 'Pricing', icon: DollarSign, count: null },
        { id: 'appusers' as const, label: 'App Users', icon: Users, count: appUsers.length },
    ];

    const getSingularLabel = (tab: typeof activeTab) => {
        const labels: Record<typeof activeTab, string> = {
            counties: 'County',
            towns: 'Town',
            markets: 'Market',
            locations: 'Location',
            routes: 'Route',
            pricing: 'Pricing Config',
            appusers: 'App User',
        };
        return labels[tab];
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Shipping Locations</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Manage counties, towns, markets, and pickup/dropoff locations across Kenya
                    </p>
                </div>
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    <span>Add {getSingularLabel(activeTab)}</span>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                        <div
                            key={tab.id}
                            className={`bg-white rounded-lg border p-3 transition-all cursor-pointer ${activeTab === tab.id ? 'border-black ring-1 ring-black' : 'border-gray-200 hover:border-gray-300'
                                }`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-500 font-medium">{tab.label}</p>
                                    <p className="text-xl font-bold text-gray-900 mt-1">{tab.count ?? '—'}</p>
                                </div>
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activeTab === tab.id ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Content Area */}
            <div className="bg-white rounded-lg border border-gray-200">
                <div className="border-b border-gray-200">
                    <div className="flex items-center justify-between p-4 flex-wrap gap-4">
                        <nav className="flex gap-1 bg-gray-100 p-1 rounded-xl">
                            {tabs.map((tab) => {
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                                            ? 'bg-white text-black shadow-sm'
                                            : 'text-gray-500 hover:text-gray-900'
                                            }`}
                                    >
                                        <span>{tab.label}</span>
                                    </button>
                                );
                            })}
                        </nav>

                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder={`Search ${activeTab}...`}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-black transition-all"
                            />
                        </div>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="p-0">
                    {activeTab === 'counties' && (
                        <CountiesTab
                            data={counties}
                            isLoading={isLoadingCounties}
                            search={search}
                            onEdit={handleEdit}
                            onDelete={(id, name) => handleDelete('county', id, name)}
                            onToggleStatus={(item) => handleToggleStatus('county', item)}
                        />
                    )}
                    {activeTab === 'towns' && (
                        <TownsTab
                            data={towns}
                            isLoading={isLoadingTowns}
                            search={search}
                            onEdit={handleEdit}
                            onDelete={(id, name) => handleDelete('town', id, name)}
                            onToggleStatus={(item) => handleToggleStatus('town', item)}
                        />
                    )}
                    {activeTab === 'markets' && (
                        <MarketsTab
                            data={markets}
                            isLoading={isLoadingMarkets}
                            search={search}
                            onEdit={handleEdit}
                            onDelete={(id, name) => handleDelete('market', id, name)}
                            onToggleStatus={(item) => handleToggleStatus('market', item)}
                        />
                    )}
                    {activeTab === 'locations' && (
                        <LocationsTab
                            data={locations}
                            isLoading={isLoadingLocations}
                            search={search}
                            onEdit={handleEdit}
                            onDelete={(id, name) => handleDelete('location', id, name)}
                            onToggleStatus={(item) => handleToggleStatus('location', item)}
                        />
                    )}
                    {activeTab === 'routes' && (
                        <RoutesTab
                            data={routes}
                            isLoading={isLoadingRoutes}
                            search={search}
                            onEdit={handleEdit}
                            onDelete={(id, name) => {
                                if (window.confirm(`Delete route "${name}"? This cannot be undone.`)) {
                                    deleteRouteMutation.mutate(id);
                                }
                            }}
                        />
                    )}
                    {activeTab === 'pricing' && (
                        <PricingConfigTab
                            config={pricingConfig}
                            onEdit={() => { setEditingItem(pricingConfig); setIsAddModalOpen(true); }}
                        />
                    )}
                    {activeTab === 'appusers' && (
                        <AppUsersTab
                            data={appUsers}
                            isLoading={isLoadingAppUsers}
                            search={search}
                            onEdit={handleEdit}
                            onDelete={(id, name) => {
                                if (window.confirm(`Deactivate user "${name}"?`)) {
                                    deleteAppUserMutation.mutate(id);
                                }
                            }}
                            onResetPin={(id) => {
                                const pin = window.prompt('Enter new 4-digit PIN:');
                                if (pin && /^\d{4}$/.test(pin)) resetPinMutation.mutate({ id, newPin: pin });
                                else if (pin) alert('PIN must be exactly 4 digits');
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Payout trigger */}
            {activeTab === 'appusers' && (
                <div className="flex justify-end">
                    <button
                        onClick={() => runPayoutMutation.mutate()}
                        disabled={runPayoutMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                        <Wallet className="w-4 h-4" />
                        {runPayoutMutation.isPending ? 'Processing...' : 'Run Payout Batch'}
                    </button>
                </div>
            )}

            {/* Add Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b">
                            <h3 className="text-xl font-bold">
                                {editingItem ? 'Edit' : 'Add New'} {getSingularLabel(activeTab)}
                            </h3>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6">
                            {activeTab === 'counties' && (
                                <CountyForm
                                    initialData={editingItem}
                                    onSubmit={(data) => editingItem ? updateCountyMutation.mutate({ id: editingItem.id, data }) : createCountyMutation.mutate(data)}
                                    isPending={editingItem ? updateCountyMutation.isPending : createCountyMutation.isPending}
                                />
                            )}
                            {activeTab === 'towns' && (
                                <TownForm
                                    initialData={editingItem}
                                    counties={counties}
                                    onSubmit={(data) => editingItem ? updateTownMutation.mutate({ id: editingItem.id, data }) : createTownMutation.mutate(data)}
                                    isPending={editingItem ? updateTownMutation.isPending : createTownMutation.isPending}
                                />
                            )}
                            {activeTab === 'markets' && (
                                <MarketForm
                                    initialData={editingItem}
                                    towns={towns}
                                    onSubmit={(data) => editingItem ? updateMarketMutation.mutate({ id: editingItem.id, data }) : createMarketMutation.mutate(data)}
                                    isPending={editingItem ? updateMarketMutation.isPending : createMarketMutation.isPending}
                                />
                            )}
                            {activeTab === 'locations' && (
                                <LocationForm
                                    initialData={editingItem}
                                    counties={counties}
                                    towns={towns}
                                    markets={markets}
                                    onSubmit={(data) => editingItem ? updateLocationMutation.mutate({ id: editingItem.id, data }) : createLocationMutation.mutate(data)}
                                    isPending={editingItem ? updateLocationMutation.isPending : createLocationMutation.isPending}
                                />
                            )}
                            {activeTab === 'routes' && (
                                <RouteForm
                                    initialData={editingItem}
                                    locations={locations}
                                    onSubmit={(data) => editingItem
                                        ? updateRouteMutation.mutate({ id: editingItem.id, data })
                                        : createRouteMutation.mutate(data)}
                                    isPending={editingItem ? updateRouteMutation.isPending : createRouteMutation.isPending}
                                />
                            )}
                            {activeTab === 'pricing' && (
                                <PricingForm
                                    initialData={editingItem}
                                    onSubmit={(data) => updatePricingMutation.mutate(data)}
                                    isPending={updatePricingMutation.isPending}
                                />
                            )}
                            {activeTab === 'appusers' && (
                                <AppUserForm
                                    initialData={editingItem}
                                    locations={locations}
                                    onSubmit={(data) => editingItem
                                        ? updateAppUserMutation.mutate({ id: editingItem.id, data })
                                        : createAppUserMutation.mutate(data)}
                                    isPending={editingItem ? updateAppUserMutation.isPending : createAppUserMutation.isPending}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CountyForm({ initialData, onSubmit, isPending }: { initialData?: any, onSubmit: (data: any) => void, isPending: boolean }) {
    const [formData, setFormData] = useState({ name: '', code: '', region: 'Nairobi', isActive: true });

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name,
                code: initialData.code,
                region: initialData.region,
                isActive: initialData.isActive
            });
        }
    }, [initialData]);

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">County Name</label>
                <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none"
                    placeholder="e.g. Nairobi"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">County Code</label>
                <input
                    type="text"
                    required
                    maxLength={3}
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none"
                    placeholder="e.g. 047"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Region</label>
                <select
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none"
                >
                    <option value="Nairobi">Nairobi</option>
                    <option value="Central">Central</option>
                    <option value="Coast">Coast</option>
                    <option value="Eastern">Eastern</option>
                    <option value="North Eastern">North Eastern</option>
                    <option value="Nyanza">Nyanza</option>
                    <option value="Rift Valley">Rift Valley</option>
                    <option value="Western">Western</option>
                </select>
            </div>
            <button
                type="submit"
                disabled={isPending}
                className="w-full py-3 bg-black text-white rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400"
            >
                {isPending ? 'Saving...' : initialData ? 'Update County' : 'Save County'}
            </button>
        </form>
    );
}

function TownForm({ initialData, counties, onSubmit, isPending }: { initialData?: any, counties: County[], onSubmit: (data: any) => void, isPending: boolean }) {
    const [formData, setFormData] = useState({ countyId: '', name: '', description: '', isActive: true });

    useEffect(() => {
        if (initialData) {
            setFormData({
                countyId: initialData.countyId,
                name: initialData.name,
                description: initialData.description || '',
                isActive: initialData.isActive
            });
        }
    }, [initialData]);

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Select County</label>
                <select
                    required
                    value={formData.countyId}
                    onChange={(e) => setFormData({ ...formData, countyId: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none"
                >
                    <option value="">Choose a county...</option>
                    {counties.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Town Name</label>
                <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none"
                    placeholder="e.g. Westlands"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Description (Optional)</label>
                <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none h-24"
                />
            </div>
            <button
                type="submit"
                disabled={isPending}
                className="w-full py-3 bg-black text-white rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400"
            >
                {isPending ? 'Saving...' : initialData ? 'Update Town' : 'Save Town'}
            </button>
        </form>
    );
}

function MarketForm({ initialData, towns, onSubmit, isPending }: { initialData?: any, towns: Town[], onSubmit: (data: any) => void, isPending: boolean }) {
    const [formData, setFormData] = useState({ townId: '', name: '', description: '', isActive: true });

    useEffect(() => {
        if (initialData) {
            setFormData({
                townId: initialData.townId,
                name: initialData.name,
                description: initialData.description || '',
                isActive: initialData.isActive
            });
        }
    }, [initialData]);

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">Select Town</label>
                <select
                    required
                    value={formData.townId}
                    onChange={(e) => setFormData({ ...formData, townId: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none"
                >
                    <option value="">Choose a town...</option>
                    {towns.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Market Name</label>
                <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none"
                    placeholder="e.g. Gikomba Market"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700">Description (Optional)</label>
                <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none h-20"
                />
            </div>
            <button
                type="submit"
                disabled={isPending}
                className="w-full py-3 bg-black text-white rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400"
            >
                {isPending ? 'Saving...' : initialData ? 'Update Market' : 'Save Market'}
            </button>
        </form>
    );
}

function LocationForm({ initialData, counties, towns, markets, onSubmit, isPending }: {
    initialData?: any,
    counties: County[],
    towns: Town[],
    markets: Market[],
    onSubmit: (data: any) => void,
    isPending: boolean
}) {
    const [formData, setFormData] = useState({
        countyId: '',
        townId: '',
        marketId: '',
        name: '',
        type: 'BOTH' as const,
        address: '',
        landmark: '',
        contactPhone: '',
        operatingHours: '',
        instructions: '',
        latitude: '',
        longitude: '',
        isActive: true
    });

    useEffect(() => {
        if (initialData) {
            setFormData({
                countyId: initialData.countyId,
                townId: initialData.townId,
                marketId: initialData.marketId || '',
                name: initialData.name,
                type: initialData.type,
                address: initialData.address,
                landmark: initialData.landmark || '',
                contactPhone: initialData.contactPhone || '',
                operatingHours: initialData.operatingHours || '',
                instructions: initialData.instructions || '',
                latitude: initialData.latitude ? String(initialData.latitude) : '',
                longitude: initialData.longitude ? String(initialData.longitude) : '',
                isActive: initialData.isActive
            });
        }
    }, [initialData]);

    // Filtered lists for UX
    const filteredTowns = towns.filter(t => t.countyId === formData.countyId);
    const filteredMarkets = markets.filter(m => m.townId === formData.townId);

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">County</label>
                    <select
                        required
                        value={formData.countyId}
                        onChange={(e) => setFormData({ ...formData, countyId: e.target.value, townId: '', marketId: '' })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                    >
                        <option value="">Select...</option>
                        {counties.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Town</label>
                    <select
                        required
                        value={formData.townId}
                        onChange={(e) => setFormData({ ...formData, townId: e.target.value, marketId: '' })}
                        disabled={!formData.countyId}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm disabled:bg-gray-50"
                    >
                        <option value="">Select...</option>
                        {filteredTowns.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Market (Optional)</label>
                <select
                    value={formData.marketId}
                    onChange={(e) => setFormData({ ...formData, marketId: e.target.value })}
                    disabled={!formData.townId}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm disabled:bg-gray-50"
                >
                    <option value="">Standalone Location</option>
                    {filteredMarkets.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Location Name</label>
                    <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. Westlands Mall Pickup"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                    >
                        <option value="PICKUP">Pickup Only</option>
                        <option value="DROPOFF">Dropoff Only</option>
                        <option value="BOTH">Both</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                    <input
                        type="tel"
                        value={formData.contactPhone}
                        onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Full Address/Street</label>
                <input
                    type="text"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Landmark</label>
                    <input
                        type="text"
                        value={formData.landmark}
                        onChange={(e) => setFormData({ ...formData, landmark: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. Near Sarit Centre"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Operating Hours</label>
                    <input
                        type="text"
                        value={formData.operatingHours}
                        onChange={(e) => setFormData({ ...formData, operatingHours: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. Mon-Sat: 8AM-6PM"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Latitude</label>
                    <input
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. -1.2921"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Longitude</label>
                    <input
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. 36.8219"
                    />
                </div>
            </div>
            <p className="text-xs text-gray-400 -mt-3">GPS coordinates are used by the routing engine to find nearest nodes. Leave blank if unknown.</p>

            <button
                type="submit"
                disabled={isPending}
                className="w-full py-3 bg-black text-white rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400"
            >
                {isPending ? 'Saving...' : initialData ? 'Update Location' : 'Save Location'}
            </button>
        </form>
    );
}

function CountiesTab({ data, isLoading, search, onEdit, onDelete, onToggleStatus }: {
    data: County[],
    isLoading: boolean,
    search: string,
    onEdit: (item: any) => void,
    onDelete: (id: string, name: string) => void,
    onToggleStatus: (item: any) => void
}) {
    const filteredData = useMemo(() => {
        return data.filter(item =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.code.toLowerCase().includes(search.toLowerCase()) ||
            item.region.toLowerCase().includes(search.toLowerCase())
        );
    }, [data, search]);

    if (isLoading) {
        return (
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">County Name</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Code</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Region</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Towns</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 animate-pulse">
                        {[...Array(8)].map((_, i) => (
                            <tr key={i}>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-12"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-5 bg-gray-200 rounded-full w-16"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">County Name</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Code</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Region</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Towns</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {filteredData.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <span className="text-sm font-semibold text-gray-900">{item.name}</span>
                            </td>
                            <td className="px-6 py-4">
                                <span className="text-sm font-mono text-gray-500 uppercase">{item.code}</span>
                            </td>
                            <td className="px-6 py-4">
                                <span className="text-sm text-gray-700">{item.region}</span>
                            </td>
                            <td className="px-6 py-4">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {item._count?.towns || 0} towns
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                {item.isActive ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Active
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                                        <XCircle className="w-3.5 h-3.5" />
                                        Inactive
                                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => onToggleStatus(item)}
                                        className={`p-1 rounded ${item.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                                        title={item.isActive ? 'Deactivate' : 'Activate'}
                                    >
                                        {item.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                    </button>
                                    <button
                                        onClick={() => onEdit(item)}
                                        className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(item.id, item.name)}
                                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {filteredData.length === 0 && (
                        <tr>
                            <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                <MapPin className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm font-medium">No counties found</p>
                                <p className="text-xs">Try adjusting your search terms</p>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function TownsTab({ data, isLoading, search, onEdit, onDelete, onToggleStatus }: {
    data: Town[],
    isLoading: boolean,
    search: string,
    onEdit: (item: any) => void,
    onDelete: (id: string, name: string) => void,
    onToggleStatus: (item: any) => void
}) {
    const filteredData = useMemo(() => {
        return data.filter(item =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.county?.name?.toLowerCase().includes(search.toLowerCase())
        );
    }, [data, search]);

    if (isLoading) {
        return (
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Town Name</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">County</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Markets</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 animate-pulse">
                        {[...Array(8)].map((_, i) => (
                            <tr key={i}>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                                    <div className="h-3 bg-gray-200 rounded w-48"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Town Name</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">County</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Markets</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {filteredData.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <span className="text-sm font-semibold text-gray-900">{item.name}</span>
                                {item.description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                                )}
                            </td>
                            <td className="px-6 py-4">
                                <span className="text-sm text-gray-700">{item.county?.name}</span>
                            </td>
                            <td className="px-6 py-4">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {item._count?.markets || 0} markets
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                {item.isActive ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Active
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                                        <XCircle className="w-3.5 h-3.5" />
                                        Inactive
                                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => onToggleStatus(item)}
                                            className={`p-1 rounded ${item.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                                            title={item.isActive ? 'Deactivate' : 'Activate'}
                                        >
                                            {item.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                        </button>
                                        <button
                                            onClick={() => onEdit(item)}
                                            className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => onDelete(item.id, item.name)}
                                            className="p-1 hover:bg-gray-100 rounded text-red-600"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function MarketsTab({ data, isLoading, search, onEdit, onDelete, onToggleStatus }: {
    data: Market[],
    isLoading: boolean,
    search: string,
    onEdit: (item: any) => void,
    onDelete: (id: string, name: string) => void,
    onToggleStatus: (item: any) => void
}) {
    const filteredData = useMemo(() => {
        return data.filter(item =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.town?.name?.toLowerCase().includes(search.toLowerCase())
        );
    }, [data, search]);

    if (isLoading) {
        return (
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Market Name</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Town</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Locations</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 animate-pulse">
                        {[...Array(8)].map((_, i) => (
                            <tr key={i}>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-40 mb-1"></div>
                                    <div className="h-3 bg-gray-200 rounded w-56"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-5 bg-gray-200 rounded-full w-24"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Market Name</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Town / County</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Locations</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {filteredData.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <span className="text-sm font-semibold text-gray-900">{item.name}</span>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-gray-700">
                                    {item.town?.name}
                                    <span className="text-gray-400 mx-1">/</span>
                                    <span className="text-gray-500">{item.town?.county?.name}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {item._count?.locations || 0} locations
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                {item.isActive ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Active
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                                        <XCircle className="w-3.5 h-3.5" />
                                        Inactive
                                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => onToggleStatus(item)}
                                        className={`p-1 rounded ${item.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                                        title={item.isActive ? 'Deactivate' : 'Activate'}
                                    >
                                        {item.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                    </button>
                                    <button
                                        onClick={() => onEdit(item)}
                                        className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(item.id, item.name)}
                                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function LocationsTab({ data, isLoading, search, onEdit, onDelete, onToggleStatus }: {
    data: PickupDropoffLocation[],
    isLoading: boolean,
    search: string,
    onEdit: (item: any) => void,
    onDelete: (id: string, name: string) => void,
    onToggleStatus: (item: any) => void
}) {
    const filteredData = useMemo(() => {
        return data.filter(item =>
            item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.address.toLowerCase().includes(search.toLowerCase()) ||
            item.market?.name?.toLowerCase().includes(search.toLowerCase())
        );
    }, [data, search]);

    if (isLoading) {
        return (
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Location</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Type</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Contact</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Market / Town</th>
                            <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 animate-pulse">
                        {[...Array(8)].map((_, i) => (
                            <tr key={i}>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-48 mb-1"></div>
                                    <div className="h-3 bg-gray-200 rounded w-64"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-5 bg-gray-200 rounded-full w-16"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                        <div className="h-6 w-6 bg-gray-200 rounded"></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Location</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Type</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Contact</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Market / Town</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {filteredData.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <div className="max-w-xs">
                                    <span className="text-sm font-semibold text-gray-900 block">{item.name}</span>
                                    <span className="text-xs text-gray-500 line-clamp-1">{item.address}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${item.type === 'PICKUP' ? 'bg-blue-100 text-blue-700' :
                                    item.type === 'DROPOFF' ? 'bg-orange-100 text-orange-700' :
                                        'bg-purple-100 text-purple-700'
                                    }`}>
                                    {item.type}
                                </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                                {item.contactPhone || 'N/A'}
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-gray-700">
                                    {item.market?.name || 'No Market'}
                                    <span className="text-xs text-gray-400 block">{item.market?.town?.name}</span>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                {item.isActive ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Active
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400">
                                        <XCircle className="w-3.5 h-3.5" />
                                        Inactive
                                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => onToggleStatus(item)}
                                        className={`p-1 rounded ${item.isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                                        title={item.isActive ? 'Deactivate' : 'Activate'}
                                    >
                                        {item.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                    </button>
                                    <button
                                        onClick={() => onEdit(item)}
                                        className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(item.id, item.name)}
                                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ============================================================
// ROUTES TAB
// ============================================================
function RoutesTab({ data, isLoading, search, onEdit, onDelete }: {
    data: any[];
    isLoading: boolean;
    search: string;
    onEdit: (item: any) => void;
    onDelete: (id: string, name: string) => void;
}) {
    const filtered = useMemo(() =>
        data.filter(r =>
            r.fromLocation?.name?.toLowerCase().includes(search.toLowerCase()) ||
            r.toLocation?.name?.toLowerCase().includes(search.toLowerCase())
        ), [data, search]);

    if (isLoading) return (
        <div className="p-8 text-center text-gray-400 animate-pulse">
            <Route className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm">Loading routes...</p>
        </div>
    );

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">From &rarr; To</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Distance</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Est. Time</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Carrier</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Direction</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {filtered.map(r => {
                        const hrs = Math.floor(r.estimatedMinutes / 60);
                        const mins = r.estimatedMinutes % 60;
                        return (
                            <tr key={r.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                        <span>{r.fromLocation?.name}</span>
                                        <span className="text-gray-400 text-xs">&rarr;</span>
                                        <span>{r.toLocation?.name}</span>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-0.5">{r.fromLocation?.address}</p>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">{Number(r.distanceKm).toFixed(1)} km</td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                    {hrs > 0 ? `${hrs}h ` : ''}{mins}m
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500">{r.carrierName || '—'}</td>
                                <td className="px-6 py-4">
                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${r.isBidirectional ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {r.isBidirectional ? '↔ Both ways' : '→ One-way'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    {r.isActive
                                        ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700"><CheckCircle2 className="w-3.5 h-3.5" />Active</span>
                                        : <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400"><XCircle className="w-3.5 h-3.5" />Inactive</span>}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button onClick={() => onEdit(r)} className="p-1 hover:bg-gray-100 rounded text-gray-600" title="Edit">
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => onDelete(r.id, `${r.fromLocation?.name} \u2192 ${r.toLocation?.name}`)} className="p-1 hover:bg-gray-100 rounded text-red-600" title="Delete">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    {filtered.length === 0 && (
                        <tr>
                            <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                                <Route className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm font-medium">No shipping routes configured yet</p>
                                <p className="text-xs mt-0.5">Click &ldquo;Add Route&rdquo; to connect two pickup nodes</p>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function RouteForm({ initialData, locations, onSubmit, isPending }: {
    initialData?: any;
    locations: any[];
    onSubmit: (data: any) => void;
    isPending: boolean;
}) {
    const [form, setForm] = useState({
        fromLocationId: '',
        toLocationId: '',
        distanceKm: '',
        estimatedMinutes: '',
        isBidirectional: true,
        carrierName: '',
        overrideCostPerKg: '',
    });

    useEffect(() => {
        if (initialData) {
            setForm({
                fromLocationId: initialData.fromLocationId,
                toLocationId: initialData.toLocationId,
                distanceKm: String(initialData.distanceKm),
                estimatedMinutes: String(initialData.estimatedMinutes),
                isBidirectional: initialData.isBidirectional,
                carrierName: initialData.carrierName || '',
                overrideCostPerKg: initialData.overrideCostPerKg ? String(initialData.overrideCostPerKg) : '',
            });
        }
    }, [initialData]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit({
            fromLocationId: form.fromLocationId,
            toLocationId: form.toLocationId,
            distanceKm: parseFloat(form.distanceKm),
            estimatedMinutes: parseInt(form.estimatedMinutes),
            isBidirectional: form.isBidirectional,
            carrierName: form.carrierName || undefined,
            overrideCostPerKg: form.overrideCostPerKg ? parseFloat(form.overrideCostPerKg) : undefined,
        });
    };

    const activeLocations = locations.filter(l => l.isActive);

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">From Node</label>
                    <select required value={form.fromLocationId}
                        onChange={e => setForm({ ...form, fromLocationId: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm">
                        <option value="">Select origin...</option>
                        {activeLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">To Node</label>
                    <select required value={form.toLocationId}
                        onChange={e => setForm({ ...form, toLocationId: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm">
                        <option value="">Select destination...</option>
                        {activeLocations.filter(l => l.id !== form.fromLocationId).map(l => (
                            <option key={l.id} value={l.id}>{l.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Distance (km)</label>
                    <input type="number" required min="0.1" step="0.1" value={form.distanceKm}
                        onChange={e => setForm({ ...form, distanceKm: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. 45.5" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Est. Time (minutes)</label>
                    <input type="number" required min="1" step="1" value={form.estimatedMinutes}
                        onChange={e => setForm({ ...form, estimatedMinutes: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. 90" />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Carrier / Vehicle</label>
                    <input type="text" value={form.carrierName}
                        onChange={e => setForm({ ...form, carrierName: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. Easy Coach, Matatu" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Override Cost /kg (KES)</label>
                    <input type="number" min="0" step="0.01" value={form.overrideCostPerKg}
                        onChange={e => setForm({ ...form, overrideCostPerKg: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="Leave blank for global pricing" />
                </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer"
                onClick={() => setForm({ ...form, isBidirectional: !form.isBidirectional })}>
                <input type="checkbox" readOnly checked={form.isBidirectional} className="w-4 h-4 accent-black pointer-events-none" />
                <div>
                    <p className="text-sm font-medium text-gray-700">Bidirectional route</p>
                    <p className="text-xs text-gray-400">Packages can travel both ways along this route</p>
                </div>
            </div>

            <button type="submit" disabled={isPending}
                className="w-full py-3 bg-black text-white rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400">
                {isPending ? 'Saving...' : initialData ? 'Update Route' : 'Create Route'}
            </button>
        </form>
    );
}

// ============================================================
// PRICING CONFIG TAB
// ============================================================
function PricingConfigTab({ config, onEdit }: { config: any; onEdit: () => void }) {
    if (!config) return (
        <div className="p-10 text-center">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm font-medium text-gray-500">No pricing configuration found</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">Defaults will be seeded automatically on first shipment calculation</p>
            <button onClick={onEdit} className="px-4 py-2 bg-black text-white rounded-lg text-sm font-medium">
                Configure Now
            </button>
        </div>
    );

    const rows = [
        { label: 'Base Fee', value: `KES ${Number(config.baseFee).toFixed(2)}`, desc: 'Fixed fee on every shipment' },
        { label: 'Rate per km', value: `KES ${Number(config.ratePerKm).toFixed(2)}/km`, desc: 'Distance-based charge' },
        { label: 'Rate per kg', value: `KES ${Number(config.ratePerKg).toFixed(2)}/kg`, desc: 'Weight-based charge' },
        { label: 'Free Weight', value: `${config.freeWeightKg} kg`, desc: 'Weight included in base fee' },
        { label: 'Excess Rate', value: `KES ${Number(config.excessRatePerKg).toFixed(2)}/kg`, desc: 'Per kg above free weight' },
        { label: 'Express Multiplier', value: `\u00d7${config.expressMultiplier}`, desc: 'Applied for express tier' },
        { label: 'Remote Multiplier', value: `\u00d7${config.remoteMultiplier}`, desc: 'Applied for inter-county routes' },
    ];

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h3 className="text-base font-semibold text-gray-900">Global Pricing Formula</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Applied to all shipments unless a specific route overrides the cost/kg</p>
                </div>
                <button onClick={onEdit}
                    className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                    <Edit2 className="w-3.5 h-3.5" />Edit Config
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {rows.map(r => (
                    <div key={r.label} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-xs text-gray-500 font-medium">{r.label}</p>
                        <p className="text-lg font-bold text-gray-900 mt-1">{r.value}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{r.desc}</p>
                    </div>
                ))}
            </div>

            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-xs font-bold text-blue-700 mb-2 uppercase tracking-wide">How cost is calculated</p>
                <code className="text-xs text-blue-900 block leading-relaxed">
                    cost = baseFee<br />
                    &nbsp;&nbsp;+ (distanceKm &times; ratePerKm)<br />
                    &nbsp;&nbsp;+ (weightKg &times; ratePerKg)<br />
                    &nbsp;&nbsp;+ max(0, weightKg &minus; freeWeightKg) &times; excessRatePerKg
                </code>
                <p className="text-xs text-blue-600 mt-2">
                    Then &times; expressMultiplier if express, or &times; remoteMultiplier if inter-county.
                </p>
            </div>
        </div>
    );
}

function PricingForm({ initialData, onSubmit, isPending }: {
    initialData?: any;
    onSubmit: (data: any) => void;
    isPending: boolean;
}) {
    const [form, setForm] = useState({
        baseFee: '50', ratePerKm: '3', ratePerKg: '20', freeWeightKg: '1',
        excessRatePerKg: '30', expressMultiplier: '1.5', remoteMultiplier: '1.3',
    });

    useEffect(() => {
        if (initialData) {
            setForm({
                baseFee: String(initialData.baseFee),
                ratePerKm: String(initialData.ratePerKm),
                ratePerKg: String(initialData.ratePerKg),
                freeWeightKg: String(initialData.freeWeightKg),
                excessRatePerKg: String(initialData.excessRatePerKg),
                expressMultiplier: String(initialData.expressMultiplier),
                remoteMultiplier: String(initialData.remoteMultiplier),
            });
        }
    }, [initialData]);

    const f = (key: keyof typeof form, label: string, desc: string, step = '0.01') => (
        <div key={key}>
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <p className="text-xs text-gray-400 mb-1">{desc}</p>
            <input type="number" min="0" step={step} value={form[key]}
                onChange={e => setForm({ ...form, [key]: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm" />
        </div>
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit({
            baseFee: parseFloat(form.baseFee),
            ratePerKm: parseFloat(form.ratePerKm),
            ratePerKg: parseFloat(form.ratePerKg),
            freeWeightKg: parseFloat(form.freeWeightKg),
            excessRatePerKg: parseFloat(form.excessRatePerKg),
            expressMultiplier: parseFloat(form.expressMultiplier),
            remoteMultiplier: parseFloat(form.remoteMultiplier),
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                {f('baseFee', 'Base Fee (KES)', 'Fixed cost per shipment')}
                {f('freeWeightKg', 'Free Weight (kg)', 'Weight included in base fee', '0.5')}
                {f('ratePerKm', 'Rate per km (KES)', 'Distance charge')}
                {f('ratePerKg', 'Rate per kg (KES)', 'Base weight charge')}
                {f('excessRatePerKg', 'Excess Rate /kg (KES)', 'Per kg above free weight')}
                {f('expressMultiplier', 'Express Multiplier', 'e.g. 1.5 = +50%', '0.05')}
            </div>
            {f('remoteMultiplier', 'Remote / Inter-county Multiplier', 'Applied when pickup and dropoff are in different towns', '0.05')}
            <button type="submit" disabled={isPending}
                className="w-full py-3 bg-black text-white rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400">
                {isPending ? 'Saving...' : 'Update Pricing Config'}
            </button>
        </form>
    );
}

// ============================================================
// APP USERS TAB
// ============================================================
function AppUsersTab({ data, isLoading, search, onEdit, onDelete, onResetPin }: {
    data: any[];
    isLoading: boolean;
    search: string;
    onEdit: (item: any) => void;
    onDelete: (id: string, name: string) => void;
    onResetPin: (id: string) => void;
}) {
    const filtered = useMemo(() => data.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.phone?.includes(search) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.location?.name?.toLowerCase().includes(search.toLowerCase())
    ), [data, search]);

    if (isLoading) return (
        <div className="p-8 text-center text-gray-400 animate-pulse">
            <Users className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm">Loading app users...</p>
        </div>
    );

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Name</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Phone</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Role</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Assigned Node</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Last Login</th>
                        <th className="px-6 py-3 text-xs font-bold text-gray-900 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-900 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {filtered.map(u => (
                        <tr key={u.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <div className="flex flex-col">
                                    <span className="text-sm font-semibold text-gray-900">{u.name}</span>
                                    {u.email && <span className="text-xs text-gray-400">{u.email}</span>}
                                </div>
                            </td>
                            <td className="px-6 py-4 text-sm font-mono text-gray-600">{u.phone}</td>
                            <td className="px-6 py-4">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${u.role === 'NODE_AGENT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                    {u.role === 'NODE_AGENT' ? 'Node Agent' : 'Shipping Agent'}
                                </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">{u.location?.name || '—'}</td>
                            <td className="px-6 py-4 text-xs text-gray-400">
                                {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never'}
                            </td>
                            <td className="px-6 py-4">
                                {u.isActive
                                    ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700"><CheckCircle2 className="w-3.5 h-3.5" />Active</span>
                                    : <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400"><XCircle className="w-3.5 h-3.5" />Inactive</span>}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <div className="flex justify-end gap-1">
                                    <button onClick={() => onResetPin(u.id)} title="Reset PIN"
                                        className="p-1 hover:bg-amber-50 rounded text-amber-600">
                                        <RefreshCw className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => onEdit(u)} title="Edit user"
                                        className="p-1 hover:bg-gray-100 rounded text-gray-600">
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => onDelete(u.id, u.name)} title="Deactivate user"
                                        className="p-1 hover:bg-red-50 rounded text-red-600">
                                        <Ban className="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {filtered.length === 0 && (
                        <tr>
                            <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                                <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                                <p className="text-sm font-medium">No app users yet</p>
                                <p className="text-xs mt-0.5">Create node agents and shipping agents to get started</p>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function AppUserForm({ initialData, locations, onSubmit, isPending }: {
    initialData?: any;
    locations: any[];
    onSubmit: (data: any) => void;
    isPending: boolean;
}) {
    const [form, setForm] = useState({
        name: '',
        phone: '',
        email: '',
        pin: '',
        role: 'NODE_AGENT',
        locationId: '',
        isActive: true,
    });

    useEffect(() => {
        if (initialData) {
            setForm({
                name: initialData.name,
                phone: initialData.phone,
                email: initialData.email || '',
                pin: '',
                role: initialData.role,
                locationId: initialData.locationId || '',
                isActive: initialData.isActive,
            });
        }
    }, [initialData]);

    const activeLocations = locations.filter(l => l.isActive);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const payload: any = { ...form };
        if (!payload.locationId) delete payload.locationId;
        if (!payload.pin) delete payload.pin;
        onSubmit(payload);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <input required type="text" value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. Jane Wanjiku" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Phone (login ID)</label>
                    <input required type="tel" value={form.phone}
                        onChange={e => setForm({ ...form, phone: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                        placeholder="e.g. 0712345678" />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700">Email (onboarding & notifications)</label>
                <input type="email" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm"
                    placeholder="e.g. jane@example.com" />
                <p className="text-[10px] text-gray-400 mt-1">
                    Login credentials will be sent to this email upon creation.
                </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value, locationId: '' })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm">
                        <option value="NODE_AGENT">Node Agent</option>
                        <option value="SHIPPING_AGENT">Shipping Agent</option>
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                        {form.role === 'NODE_AGENT' ? 'Manages check-in/out at a fixed node' : 'Transports packages between nodes'}
                    </p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        {initialData ? 'New PIN (leave blank to keep)' : 'PIN (4 digits)'}
                    </label>
                    <input type="password" inputMode="numeric" maxLength={4} minLength={4}
                        value={form.pin}
                        onChange={e => setForm({ ...form, pin: e.target.value })}
                        required={!initialData}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm tracking-widest"
                        placeholder="••••" />
                </div>
            </div>

            {form.role === 'NODE_AGENT' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700">Assigned Node</label>
                    <select value={form.locationId}
                        onChange={e => setForm({ ...form, locationId: e.target.value })}
                        className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black outline-none text-sm">
                        <option value="">No fixed node yet</option>
                        {activeLocations.map(l => (
                            <option key={l.id} value={l.id}>{l.name} &mdash; {l.address}</option>
                        ))}
                    </select>
                </div>
            )}

            {initialData && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => setForm({ ...form, isActive: !form.isActive })}>
                    <input type="checkbox" readOnly checked={form.isActive} className="w-4 h-4 accent-black pointer-events-none" />
                    <div>
                        <p className="text-sm font-medium text-gray-700">Active account</p>
                        <p className="text-xs text-gray-400">Uncheck to deactivate — user will not be able to log in</p>
                    </div>
                </div>
            )}

            <button type="submit" disabled={isPending}
                className="w-full py-3 bg-black text-white rounded-lg font-bold hover:bg-gray-800 transition-colors disabled:bg-gray-400">
                {isPending ? 'Saving...' : initialData ? 'Update User' : 'Create App User'}
            </button>
        </form>
    );
}
